/**
 * Typisierte Hooks für `/api/v1/documents` (Spec 09, `domains/documents`) —
 * die Liste über den ganzen Bestand, der Zähler für `rail-dokumente`, die
 * signierten URLs und das Registrieren samt Text (ADR 0018).
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über das Präfix
 * `keys.documents.all`, nie einzeln aufgezählte Keys — Anlegen, Ändern,
 * Zuordnen und Archivieren können Liste **und** `summary` zugleich
 * verändern (dasselbe Muster wie `features/tasks/api.ts`).
 *
 * **Eine Ausnahme von „fetch nur über `request()`":** `uploadFileToStorage`
 * (`lib/storage-upload.ts`, seit Spec 46b dort) spricht nicht die eigene API,
 * sondern die signierte Storage-URL aus `POST /documents/upload-url`.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type DocumentRow = components['schemas']['DocumentRow']
export type DocumentSubject = components['schemas']['DocumentSubject']
export type DocumentCreate = components['schemas']['DocumentCreate']
export type DocumentUpdate = components['schemas']['DocumentUpdate']
export type DocumentSummary = components['schemas']['DocumentSummary']
export type TextBlockBody = components['schemas']['TextBlockBody']
export type LabeledValue = components['schemas']['LabeledValue']
export type DocumentSuggestion = components['schemas']['DocumentSuggestion']
export type DocumentMeterSuggestion = components['schemas']['DocumentMeterSuggestion']
export type DocumentSuggestionAcceptResponse = components['schemas']['DocumentSuggestionAcceptResponse']
export type DocumentComment = components['schemas']['DocumentComment']
export type DocumentComments = components['schemas']['DocumentComments']
export type SenderRef = components['schemas']['SenderRef']
export type SenderRow = components['schemas']['SenderRow']
export type SenderCreate = components['schemas']['SenderCreate']
export type SenderUpdate = components['schemas']['SenderUpdate']

interface QueryOptions {
  readonly enabled?: boolean
}

export interface DocumentsPage {
  readonly items: DocumentRow[]
  readonly next_cursor: string | null
}

export interface DocumentsFilter {
  readonly scope?: string
  readonly unfiled?: boolean
  readonly category?: string
  readonly direction?: string
  readonly q?: string
  readonly limit?: number
  readonly cursor?: string
  readonly updatedSince?: string
}

/**
 * `GET /documents` — dieselbe Abfrage für den Reiter *Dokumente* eines
 * Objekts (`?scope=`) und für die Ansichten im Bereich *Dokumente* (`?
 * unfiled=1`, `?category=`, `?q=`). Aufbau der Anfrage **in** `queryFn` aus
 * einzelnen `filter.*`-Feldern — dieselbe Begründung wie
 * `features/tasks/api.ts::useTicketsList`: `@tanstack/query/exhaustive-deps`
 * sähe sonst nur „`filter` wird benutzt" und verlangte es unverändert im
 * `queryKey`, obwohl der Key bereits jedes Feld einzeln trägt.
 */
export function useDocumentsList(filter: DocumentsFilter, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.documents.list({
      scope: filter.scope,
      unfiled: filter.unfiled ?? false,
      category: filter.category,
      direction: filter.direction,
      q: filter.q,
      limit: filter.limit,
      cursor: filter.cursor,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      if (filter.unfiled === true) params.set('unfiled', 'true')
      if (filter.category !== undefined) params.set('category', filter.category)
      if (filter.direction !== undefined) params.set('direction', filter.direction)
      if (filter.q !== undefined && filter.q !== '') params.set('q', filter.q)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.cursor !== undefined) params.set('cursor', filter.cursor)
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<DocumentsPage>(`/documents${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

/** Seitenweise Dokumente im Objekt-Facet; die Bereichsliste bleibt bei ihrem
 * bestehenden Einzelquery-Vertrag. */
export function useInfiniteDocumentsList(filter: DocumentsFilter, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.documents.list({
      scope: filter.scope,
      unfiled: filter.unfiled ?? false,
      category: filter.category,
      direction: filter.direction,
      q: filter.q,
      limit: filter.limit,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      if (filter.unfiled === true) params.set('unfiled', 'true')
      if (filter.category !== undefined) params.set('category', filter.category)
      if (filter.direction !== undefined) params.set('direction', filter.direction)
      if (filter.q !== undefined && filter.q !== '') params.set('q', filter.q)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<DocumentsPage>(`/documents${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}

/** `GET /documents/summary` — die Kennzahl hinter `rail-dokumente-attention`
 * (ADR 0011: „Kein Bereich ohne eigenen Zähl-Endpunkt"). In 09 ist
 * `attention = unfiled` — der Server entscheidet das, nicht diese Datei. */
export function useDocumentSummary(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.documents.summary,
    queryFn: ({ signal }) => request<DocumentSummary>('/documents/summary', { signal }),
    enabled: options.enabled ?? true,
  })
}

/** `GET /documents/categories` — acht feste Arten, ändert sich zur Laufzeit
 * nie (dieselbe Zurückhaltung wie `useTicketPriorities`). */
export function useDocumentCategories() {
  return useQuery({
    queryKey: keys.documents.categories,
    queryFn: ({ signal }) => request<LabeledValue[]>('/documents/categories', { signal }),
    staleTime: Infinity,
  })
}

export type DocumentLimits = components['schemas']['DocumentLimits']

/** `GET /documents/limits` — Höchstgröße und erlaubte Typen, **die** Quelle
 * dafür (AK-500, W-B4 im gatekeeper-Bericht zu Tor 2, „Der Server liefert
 * beides jetzt"). Vorher stand `max_bytes` erst in der Antwort von
 * `POST /documents/upload-url`, nachdem ein Client bereits einen `mime_type`
 * geraten und mitgeschickt hatte — und die erlaubten Typen bekam er dabei gar
 * nicht. Diese Datei bleibt die Richtlinie; `DocumentSheet.tsx` übernimmt sie
 * nur (`accept` am Dateifeld), statt die fünf Typen selbst aufzuzählen. */
export function useDocumentLimits() {
  return useQuery({
    queryKey: keys.documents.limits,
    queryFn: ({ signal }) => request<DocumentLimits>('/documents/limits', { signal }),
    staleTime: Infinity,
  })
}

export interface SignedUpload {
  readonly upload_url: string
  readonly storage_key: string
  readonly expires_at: string
  readonly max_bytes: number
}

interface CreateUploadUrlArgs {
  readonly subjectId: string | undefined
  readonly originalFilename: string
  readonly mimeType: string
  readonly byteSize: number
}

/** `POST /documents/upload-url` — die signierte, kurzlebige und einmalige
 * URL, direkt zum Storage (AK-500). Kein `Idempotency-Key`: Ein zweiter
 * Aufruf mit denselben Angaben liefert ohnehin einen zweiten, ebenso
 * kurzlebigen `storage_key` — Idempotenz sichert stattdessen `POST
 * /documents` selbst (`useCreateDocument`). */
export function useCreateUploadUrl() {
  return useMutation({
    mutationFn: ({ subjectId, originalFilename, mimeType, byteSize }: CreateUploadUrlArgs) =>
      request<SignedUpload>('/documents/upload-url', {
        method: 'POST',
        body: {
          subject_id: subjectId ?? null,
          original_filename: originalFilename,
          mime_type: mimeType,
          byte_size: byteSize,
        },
      }),
  })
}

interface CreateDocumentArgs {
  readonly body: DocumentCreate
  readonly idempotencyKey: string
}

/** `POST /documents` — registriert die Datei **samt Text** (ADR 0018), in
 * einem einzigen Aufruf. Invalidiert `documents.all`: Liste **und**
 * `summary` können sich zugleich ändern (ein neues Dokument ohne Objekt
 * erhöht `unfiled` und damit `attention`). */
export function useCreateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body, idempotencyKey }: CreateDocumentArgs) =>
      request<DocumentRow>('/documents', { method: 'POST', body, idempotencyKey }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdateDocumentArgs {
  readonly documentId: string
  readonly body: DocumentUpdate
  readonly ifMatch: string
}

export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, body, ifMatch }: UpdateDocumentArgs) =>
      request<DocumentRow>(`/documents/${documentId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface AssignDocumentArgs {
  readonly documentId: string
  readonly subjectId: string
  readonly ifMatch: string
}

/** `POST /documents/{id}/assign` — zuordnen, `editor` genügt (Spec 09,
 * „Zuordnen ist editor, nicht manager"). Trifft `entities.all` mit: Der
 * automatische Eingangs-Grant fällt weg, `access-*`-Ansichten desselben
 * Nutzers können sich ändern. */
export function useAssignDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, subjectId, ifMatch }: AssignDocumentArgs) =>
      request<DocumentRow>(`/documents/${documentId}/assign`, {
        method: 'POST',
        body: { subject_id: subjectId },
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchiveDocumentArgs {
  readonly documentId: string
  readonly ifMatch: string
}

/** `POST /documents/{id}/archive` / `/unarchive` — dieselbe Bauform wie bei
 * Grundstück, Gebäude und Einheit (Entscheidung 5), `manager` verlangt. */
export function useArchiveDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ifMatch }: ArchiveDocumentArgs) =>
      request<DocumentRow>(`/documents/${documentId}/archive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export interface SignedDownload {
  readonly download_url: string
  readonly expires_at: string
}

/** `GET /documents/{id}/download-url` — für „Ansehen" (`document-preview`)
 * und den Sonderfall „Herunterladen" bei nicht darstellbaren Typen
 * (AK-520). Als Mutation statt `useQuery`: ein einmaliger Klick-Trigger,
 * kein Zustand, der gemountet bleiben soll (dieselbe Bauform wie
 * `useArchivePreview` in `features/properties/api.ts`). */
export function useDocumentDownloadUrl() {
  return useMutation({
    mutationFn: (documentId: string) => request<SignedDownload>(`/documents/${documentId}/download-url`),
  })
}

interface SuggestionActionArgs {
  readonly documentId: string
  readonly ifMatch: string
}

/** `POST /documents/{id}/suggestion/accept` (Spec 24c, Fachregel 6) —
 * übernimmt Absender, Art und, mit Glück, das Objekt. Trifft `senders.all`
 * mit: `document_count` am übernommenen Absender ändert sich. */
export function useAcceptSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ifMatch }: SuggestionActionArgs) =>
      request<DocumentSuggestionAcceptResponse>(`/documents/${documentId}/suggestion/accept`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      void queryClient.invalidateQueries({ queryKey: keys.senders.all })
    },
  })
}

/** `POST /documents/{id}/suggestion/dismiss` (Spec 24c, Fachregel 6) — für
 * dieses Dokument und diesen Absender endgültig. */
export function useDismissSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ifMatch }: SuggestionActionArgs) =>
      request<DocumentRow>(`/documents/${documentId}/suggestion/dismiss`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}

/** `POST /documents/{id}/meter-suggestion/accept` (Spec 46c, Regel 13) — hängt
 * das Dokument an den vorgeschlagenen Zähler; Antwort ist die frische Zeile.
 * Nur `documents.all`: die Liste am Zähler hängt an `documents.list({scope})`. */
export function useAcceptMeterSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ifMatch }: SuggestionActionArgs) =>
      request<DocumentRow>(`/documents/${documentId}/meter-suggestion/accept`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}

/** `POST /documents/{id}/meter-suggestion/dismiss` (Spec 46c, Regel 14) — für
 * dieses Dokument und diesen Zähler endgültig. */
export function useDismissMeterSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ifMatch }: SuggestionActionArgs) =>
      request<DocumentRow>(`/documents/${documentId}/meter-suggestion/dismiss`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}

// --- Kommentare (Spec 44) --------------------------------------------------
// Eigener Endpunkt, eigener Key: Kommentare ändern weder `version` noch
// `updated_at` des Dokuments (Fachregel 8) — die Mutationen berühren deshalb
// nur `keys.documents.comments(id)`, nie `documents.all`/`entities.all`.

/** `GET /documents/{id}/comments` — alle Kommentare, älteste oben, samt
 * `can_comment`. Externe bekommen 403 (`field: user`); wer das Blatt zeigt,
 * blendet den Abschnitt dann aus. */
export function useDocumentComments(documentId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.documents.comments(documentId),
    queryFn: ({ signal }) => request<DocumentComments>(`/documents/${documentId}/comments`, { signal }),
    // Andere schreiben mit: jedes Öffnen des Blatts lädt neu (Spec 41, still).
    staleTime: 0,
    enabled: options.enabled ?? true,
  })
}

interface CreateCommentArgs {
  readonly documentId: string
  readonly body: string
  readonly idempotencyKey: string
}

/** `POST /documents/{id}/comments` — sofort gesendet, ohne das Formular zu
 * speichern. Der Aufrufer hält den `Idempotency-Key` je Text (Wiederholen
 * nach Verbindungsabbruch legt keinen zweiten Kommentar an). */
export function useCreateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, body, idempotencyKey }: CreateCommentArgs) =>
      request<DocumentComment>(`/documents/${documentId}/comments`, {
        method: 'POST',
        body: { body },
        idempotencyKey,
      }),
    onSuccess: (_created, { documentId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.comments(documentId) })
    },
  })
}

interface DeleteCommentArgs {
  readonly documentId: string
  readonly commentId: string
}

/** `DELETE /documents/{id}/comments/{commentId}` — 204, ohne Rückfrage, ohne
 * `If-Match` und ohne `Idempotency-Key` (Kommentare haben keine `version`).
 * Lädt auch nach einem Fehler neu: 404 heißt, der Kommentar ist schon weg. */
export function useDeleteComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, commentId }: DeleteCommentArgs) =>
      request<undefined>(`/documents/${documentId}/comments/${commentId}`, { method: 'DELETE' }),
    onSettled: (_deleted, _error, { documentId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.documents.comments(documentId) })
    },
  })
}

// --- Absender (Spec 24c) --------------------------------------------------
// Eigene Domäne im selben Bereich (`docs/specs/24c-dokumente-absender.md`,
// „Betrifft"): Absender sind Stammdaten von *Dokumente*, keine eigene
// Ansicht/Bereich (Fachregel 1) — deshalb hier statt in einem neuen Feature.

export interface SendersPage {
  readonly items: SenderRow[]
  readonly next_cursor: string | null
}

export interface SendersFilter {
  readonly q?: string
  readonly limit?: number
  readonly cursor?: string
}

export function sendersQuery(filter: SendersFilter, pageParam?: string | null): string {
  const params = new URLSearchParams()
  // `GET /senders` verlangt `q` ab zwei Zeichen (422 darunter) — hier für
  // alle Aufrufer (Tor B 24c, Bericht 35 B1). `GET /documents` kennt keine
  // Mindestlänge (24a) und bleibt, wie es ist.
  const q = filter.q?.trim() ?? ''
  if (q.length >= 2) params.set('q', q)
  if (filter.limit !== undefined) params.set('limit', String(filter.limit))
  const cursor = pageParam ?? filter.cursor
  if (cursor !== undefined) params.set('cursor', cursor)
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

/** `GET /senders?q=` — die flache Liste hinter `SenderField` (Auswahl im
 * Dokumentblatt) **und** `senders-list` (`?view=senders`). Ohne Suchtext die
 * ersten `limit` ohne `q` (Spec 24c, Oberfläche B: „darunter die ersten 20
 * ohne q") — der Server verlangt `q` ab zwei Zeichen, also lässt der Client
 * `q` unter dieser Länge weg, statt 422 zu ernten. */
export function useSenderOptions(q: string, options: QueryOptions = {}) {
  const trimmed = q.trim()
  const filter: SendersFilter = trimmed.length >= 2 ? { q: trimmed, limit: 20 } : { limit: 20 }
  return useQuery({
    queryKey: keys.senders.list({ ...filter }),
    queryFn: ({ signal }) => request<SendersPage>(`/senders${sendersQuery(filter)}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

/** Dieselbe Liste, seitenweise für `senders-list` (`SenderList.tsx`,
 * `senders-list-more`) — dasselbe Muster wie `useInfiniteDocumentsList`. */
export function useInfiniteSendersList(filter: SendersFilter, options: QueryOptions = {}) {
  const q = filter.q?.trim() ?? ''
  // Der Query-Key folgt dem, was wirklich gesendet wird: „a" und „" sind
  // dieselbe Anfrage.
  const effective: SendersFilter = {
    ...(filter.limit !== undefined ? { limit: filter.limit } : {}),
    ...(filter.cursor !== undefined ? { cursor: filter.cursor } : {}),
    ...(q.length >= 2 ? { q } : {}),
  }
  return useInfiniteQuery({
    queryKey: keys.senders.list({ ...effective }),
    queryFn: ({ pageParam, signal }) =>
      request<SendersPage>(`/senders${sendersQuery(effective, pageParam)}`, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}

interface CreateSenderArgs {
  readonly body: SenderCreate
}

/** `POST /senders` (Spec 24c, Fachregel 2: interner Nutzer mit mindestens
 * einem Grant ≥ `editor`). Kann Vorschläge an bestehenden Eingangsdokumenten
 * auslösen (Auslöser c) — `documents.all` mit invalidiert. */
export function useCreateSender() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateSenderArgs) =>
      request<SenderRow>('/senders', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.senders.all })
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}

interface UpdateSenderArgs {
  readonly senderId: string
  readonly body: SenderUpdate
  readonly ifMatch: string
}

/** `PATCH /senders/{id}` — ein geänderter Alias oder eine geänderte IBAN kann
 * ebenfalls Auslöser (c) treffen (Fachregel 3). */
export function useUpdateSender() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ senderId, body, ifMatch }: UpdateSenderArgs) =>
      request<SenderRow>(`/senders/${senderId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.senders.all })
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}
